import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Divider } from 'primereact/divider';
import { useEffect, useState } from 'react';
import { getItem } from "../../../utils/localStorage";
import { makeRequest2 } from "../../../utils/makeRequest";
import { toastify } from "../../../utils/toast";
import { getReservationURL } from "../../../utils/urls";

const ReservationDetailsModal = ({ visible, onHide, reservationId, facilityId }) => {
  const [loading, setLoading] = useState(true);
  const [reservationResponse, setReservationResponse] = useState(null);
  const [error, setError] = useState(null);
  const [isManagedByLandlord, setIsManagedByLandlord] = useState(false);

  // Helper function to safely get reservation data
  const getReservationData = () => {
    if (!reservationResponse) return null;
    // Handle double-nesting
    if (reservationResponse.data && reservationResponse.data.data) {
      return reservationResponse.data.data;
    }
    return reservationResponse.data || null;
  };

  useEffect(() => {
    const fetchReservationDetails = async () => {
      if (!visible || !reservationId) return;

      try {
        setLoading(true);
        setError(null);

        const facilityIdToUse = facilityId || await getItem("selectedFacilityId");
        if (!facilityIdToUse) {
          throw new Error("No facility selected");
        }

        // Make API request to fetch reservation details
        const response = await makeRequest2(
          `${getReservationURL}/${facilityIdToUse}/${reservationId}`,
          "GET"
        );

        if (response.success && response.data) {
          console.log("FULL API RESPONSE STRUCTURE:", JSON.stringify(response, null, 2));
          // Store the entire response
          setReservationResponse(response);

          // Check if booking property is managed by landlord
          const reservation = response.data?.data || response.data;
          if (reservation?.bookingPropertyId) {
            try {
              const bookingPropertyResponse = await makeRequest2(
                `/api/booking-properties/${facilityIdToUse}/${reservation.bookingPropertyId._id || reservation.bookingPropertyId}`,
                "GET"
              );

              if (bookingPropertyResponse.success) {
                const bookingProperty = bookingPropertyResponse.data?.data || bookingPropertyResponse.data;
                setIsManagedByLandlord(bookingProperty.managedByLandlord || false);
              }
            } catch (propertyError) {
              console.error("Error fetching booking property details:", propertyError);
              // Default to property manager if we can't determine
              setIsManagedByLandlord(false);
            }
          }
        } else {
          throw new Error(response.error || "Failed to fetch reservation details");
        }

      } catch (error) {
        console.error("Error fetching reservation details:", error);
        setError(error.message || "An error occurred while loading reservation details");
        toastify("Failed to load reservation details", "error");
      } finally {
        setLoading(false);
      }
    };

    if (visible && reservationId) {
      fetchReservationDetails();
    }
  }, [visible, reservationId, facilityId]);

  // Formatting helpers
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      console.error("Error formatting date:", e);
      return "N/A";
    }
  };

  const formatCurrency = (amount) => {
    if (isManagedByLandlord) {
      return "Landlord Managed";
    }

    if (amount === undefined || amount === null) return "N/A";

    const reservation = getReservationData();

    try {
      const symbol = (reservation?.currencyId?.symbol ||
        reservation?.currencyId?.currencyShortCode ||
        '$');

      return `${symbol}${parseFloat(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } catch (e) {
      console.error("Error formatting currency:", e);
      return `$${amount}`;
    }
  };

  const getStatusBadge = (status, checkoutPending) => {
    const statusClasses = {
      reserved: "bg-warning",
      booked: "bg-success",
      canceled: "bg-danger",
      completed: "bg-info"
    };

    const statusDisplay = {
      reserved: "Reserved",
      booked: "Booked",
      canceled: "Canceled",
      completed: "Completed"
    };

    const normalizedStatus = status === 'new' ? 'reserved'
      : status === 'confirmed' ? 'booked'
      : status === 'cancelled' ? 'canceled'
      : status;

    if (checkoutPending) {
      return (
        <span className="status-badge bg-warning">
          Pending
        </span>
      );
    }

    return (
      <span className={`status-badge ${statusClasses[normalizedStatus] || 'bg-secondary'}`}>
        {statusDisplay[normalizedStatus] || normalizedStatus}
      </span>
    );
  };

  // Dialog footer with close button
  const renderFooter = () => {
    return (
      <div>
        <Button
          label="Close"
          icon="ti ti-x"
          className="p-button-text"
          onClick={onHide}
        />
      </div>
    );
  };

  // Dialog content based on state
  const renderContent = () => {
    if (loading) {
      return (
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <h5>Loading Reservation Details...</h5>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-5">
          <i className="ti ti-alert-circle text-danger" style={{ fontSize: '3rem' }}></i>
          <h4 className="mt-3">Error Loading Reservation</h4>
          <p className="text-muted mb-4">{error}</p>
        </div>
      );
    }

    const reservation = getReservationData();
    if (!reservation) {
      return (
        <div className="text-center py-5">
          <i className="ti ti-calendar-off text-warning" style={{ fontSize: '3rem' }}></i>
          <h4 className="mt-3">Reservation Not Found</h4>
          <p className="text-muted mb-4">The reservation you're looking for doesn't exist or has been removed.</p>
        </div>
      );
    }

    // Log reservation data to help debug
    console.log("Rendering reservation data:", reservationResponse);

    return (
      <div className="px-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">
            Reservation {reservation.bookingReservationId}
            <span className="ms-3">{getStatusBadge(reservation.status, reservation.checkoutPending)}</span>
            {isManagedByLandlord && (
              <span className="ms-2">
                <small className="badge bg-warning">Landlord Managed</small>
              </span>
            )}
          </h5>
        </div>

        <Divider className="my-3" />

        <div className="row">
          {/* Left Column */}
          <div className="col-md-6">
            {/* Reservation Info Section */}
            <div className="mb-4">
              <h6 className="mb-3">Reservation Information</h6>
              <div className="row mb-2">
                <div className="col-md-5 text-muted">Booking ID:</div>
                <div className="col-md-7 fw-medium">{reservation.bookingReservationId || 'N/A'}</div>
              </div>
              <div className="row mb-2">
                <div className="col-md-5 text-muted">Property:</div>
                <div className="col-md-7 fw-medium">
                  {reservation.bookingPropertyId?.propertyName || 'Unknown Property'}
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-md-5 text-muted">Apartment:</div>
                <div className="col-md-7 fw-medium">
                  {reservation.unitId?.name || 'Unknown Apartment'}
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-md-5 text-muted">Management:</div>
                <div className="col-md-7">
                  <span className={`badge ${isManagedByLandlord ? 'bg-warning' : 'bg-success'}`}>
                    {isManagedByLandlord ? 'Landlord Managed' : 'Property Manager'}
                  </span>
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-md-5 text-muted">Status:</div>
                <div className="col-md-7">
                  {getStatusBadge(reservation.status, reservation.checkoutPending)}
                </div>
              </div>
              {reservation.invoiceId && (
                <div className="row mb-2">
                  <div className="col-md-5 text-muted">Invoice ID:</div>
                  <div className="col-md-7 fw-medium">{reservation.invoiceId}</div>
                </div>
              )}
              {reservation.paymentTiming && (
                <div className="row mb-2">
                  <div className="col-md-5 text-muted">Payment Timing:</div>
                  <div className="col-md-7 fw-medium">{reservation.paymentTiming}</div>
                </div>
              )}
              {reservation.reservationPolicy && (
                <div className="row mb-2">
                  <div className="col-md-5 text-muted">Reservation Policy:</div>
                  <div className="col-md-7">{reservation.reservationPolicy}</div>
                </div>
              )}
              {reservation.createdAt && (
                <div className="row mb-2">
                  <div className="col-md-5 text-muted">Created:</div>
                  <div className="col-md-7">{formatDate(reservation.createdAt)}</div>
                </div>
              )}
            </div>

            {/* Stay Information */}
            <div className="mb-4">
              <h6 className="mb-3">Stay Information</h6>
              <div className="row mb-2">
                <div className="col-md-5 text-muted">Check-in:</div>
                <div className="col-md-7">{formatDate(reservation.checkIn)}</div>
              </div>
              <div className="row mb-2">
                <div className="col-md-5 text-muted">Check-out:</div>
                <div className="col-md-7">{formatDate(reservation.checkOut)}</div>
              </div>
              <div className="row mb-2">
                <div className="col-md-5 text-muted">Length of Stay:</div>
                <div className="col-md-7">
                  {reservation.calculatedFields?.nights ||
                    Math.ceil((new Date(reservation.checkOut) - new Date(reservation.checkIn)) / (1000 * 60 * 60 * 24)) || 0}
                  {(reservation.calculatedFields?.nights === 1 ||
                    Math.ceil((new Date(reservation.checkOut) - new Date(reservation.checkIn)) / (1000 * 60 * 60 * 24)) === 1)
                    ? ' night' : ' nights'}
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-md-5 text-muted">Guests:</div>
                <div className="col-md-7">
                  {reservation.guests?.adults || 1} Adults, {reservation.guests?.children || 0} Children
                </div>
              </div>
              {reservation.specialRequests && (
                <div className="row mb-2">
                  <div className="col-md-5 text-muted">Special Requests:</div>
                  <div className="col-md-7">{reservation.specialRequests}</div>
                </div>
              )}
            </div>

            {/* Checkout Information (if completed) */}
            {reservation.status === 'completed' && (
              <div className="mb-4">
                <h6 className="mb-3">Checkout Information</h6>
                <div className="row mb-2">
                  <div className="col-md-5 text-muted">Checkout Date:</div>
                  <div className="col-md-7">{formatDate(reservation.checkoutDate || reservation.checkOutActual)}</div>
                </div>
                {reservation.checkoutNotes && (
                  <div className="row mb-2">
                    <div className="col-md-5 text-muted">Checkout Notes:</div>
                    <div className="col-md-7">{reservation.checkoutNotes}</div>
                  </div>
                )}
                {!isManagedByLandlord && reservation.finalAmount && (
                  <div className="row mb-2">
                    <div className="col-md-5 text-muted">Final Amount:</div>
                    <div className="col-md-7 fw-bold">{formatCurrency(reservation.finalAmount)}</div>
                  </div>
                )}
              </div>
            )}

            {/* Cancellation Information */}
            {(reservation.status === 'canceled' || reservation.status === 'cancelled') && reservation.cancellationDetails && (
              <div className="mb-4">
                <h6 className="mb-3">Cancellation Details</h6>
                <div className="row mb-2">
                  <div className="col-md-5 text-muted">Reason:</div>
                  <div className="col-md-7">{reservation.cancellationDetails.reason || 'N/A'}</div>
                </div>
                {reservation.cancellationDetails.date && (
                  <div className="row mb-2">
                    <div className="col-md-5 text-muted">Date:</div>
                    <div className="col-md-7">{formatDate(reservation.cancellationDetails.date)}</div>
                  </div>
                )}
                {typeof reservation.cancellationDetails.refundAmount !== 'undefined' && (
                  <div className="row mb-2">
                    <div className="col-md-5 text-muted">Refund Amount:</div>
                    <div className="col-md-7 fw-medium">
                      {isManagedByLandlord
                        ? `${parseFloat(reservation.cancellationDetails.refundAmount || 0).toFixed(2)}`
                        : formatCurrency(reservation.cancellationDetails.refundAmount)}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Column */}
          <div className="col-md-6">
            {/* Guest Information */}
            <div className="mb-4">
              <h6 className="mb-3">Guest Information</h6>
              <div className="d-flex align-items-center mb-3">
                <div className="guest-avatar me-3" style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: '#4361ee',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}>
                  {reservation.guestInfo?.name ? reservation.guestInfo.name.charAt(0).toUpperCase() : 'G'}
                </div>
                <div>
                  <h6 className="mb-0">{reservation.guestInfo?.name || 'Unknown Guest'}</h6>
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-md-5 text-muted">Email:</div>
                <div className="col-md-7">{reservation.guestInfo?.email || 'N/A'}</div>
              </div>
              <div className="row mb-2">
                <div className="col-md-5 text-muted">Phone:</div>
                <div className="col-md-7">{reservation.guestInfo?.phone || 'N/A'}</div>
              </div>
              <div className="row mb-2">
                <div className="col-md-5 text-muted">ID Number:</div>
                <div className="col-md-7">{reservation.guestInfo?.idNumber || 'N/A'}</div>
              </div>
            </div>

            {/* Payment Information - Only for Property Manager */}
            {!isManagedByLandlord && (
              <div className="mb-4">
                <h6 className="mb-3">Payment Information</h6>
                <div className="row mb-2">
                  <div className="col-md-5 text-muted">Payment Method:</div>
                  <div className="col-md-7">{reservation.paymentMethod || 'N/A'}</div>
                </div>
                <div className="row mb-2">
                  <div className="col-md-5 text-muted">Payment Timing:</div>
                  <div className="col-md-7">{reservation.paymentTiming || 'N/A'}</div>
                </div>
                <div className="row mb-2">
                  <div className="col-md-5 text-muted">Base Price:</div>
                  <div className="col-md-7">
                    {typeof reservation.basePrice !== 'undefined' ? formatCurrency(reservation.basePrice) : 'N/A'}
                  </div>
                </div>
                <div className="row mb-2">
                  <div className="col-md-5 text-muted">Commission:</div>
                  <div className="col-md-7">
                    {typeof reservation.commission !== 'undefined' ? formatCurrency(reservation.commission) : 'N/A'}
                  </div>
                </div>
                <div className="row mb-2">
                  <div className="col-md-5 text-muted">Landlord Amount:</div>
                  <div className="col-md-7">
                    {typeof reservation.landlordAmount !== 'undefined' ? formatCurrency(reservation.landlordAmount) : 'N/A'}
                  </div>
                </div>
                <div className="row fw-bold mt-2">
                  <div className="col-md-5 text-muted">Total Amount:</div>
                  <div className="col-md-7 text-success">
                    {typeof reservation.totalAmount !== 'undefined' ? formatCurrency(reservation.totalAmount) : 'N/A'}
                  </div>
                </div>
                {reservation.currencyId && (
                  <div className="row mt-2">
                    <div className="col-md-5 text-muted">Currency:</div>
                    <div className="col-md-7">
                      {reservation.currencyId.currencyName || reservation.currencyId.currencyShortCode || 'N/A'}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Landlord Managed Notice */}
            {isManagedByLandlord && (
              <div className="mb-4">
                <div className="alert alert-info">
                  <i className="ti ti-info-circle me-2"></i>
                  <strong>Landlord Managed Apartment:</strong> Financial details are managed directly by the landlord.
                </div>
              </div>
            )}

            {/* Status History */}
            {reservation.statusHistory && reservation.statusHistory.length > 0 && (
              <div className="mb-4">
                <h6 className="mb-3">Status History</h6>
                <ul className="list-group">
                  {reservation.statusHistory.map((entry, index) => (
                    <li key={`${entry.status}-${index}`} className="list-group-item d-flex justify-content-between align-items-center">
                      <span>{getStatusBadge(entry.status, false)}</span>
                      <small className="text-muted">{formatDate(entry.timestamp)}</small>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Additional Services Section - Only for Property Manager */}
        {!isManagedByLandlord && reservation.additionalServices && reservation.additionalServices.length > 0 && (
          <div className="mt-3">
            <h6 className="mb-3">Additional Services</h6>
            <table className="table table-bordered table-sm">
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Quantity</th>
                  <th>Price</th>
                </tr>
              </thead>
              <tbody>
                {reservation.additionalServices.map((service, index) => (
                  <tr key={index}>
                    <td>{service.serviceName}</td>
                    <td>{service.quantity}</td>
                    <td>{formatCurrency(service.price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Additional Checkout Charges (if any) */}
        {reservation.additionalCheckoutCharges && reservation.additionalCheckoutCharges.length > 0 && (
          <div className="mt-3">
            <h6 className="mb-3">Additional Checkout Charges</h6>
            <table className="table table-bordered table-sm">
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Amount</th>
                  <th>Reason</th>
                  <th>Date Added</th>
                </tr>
              </thead>
              <tbody>
                {reservation.additionalCheckoutCharges.map((charge, index) => (
                  <tr key={index}>
                    <td>{charge.description}</td>
                    <td>
                      {isManagedByLandlord
                        ? `${parseFloat(charge.amount).toFixed(2)}`
                        : formatCurrency(charge.amount)
                      }
                    </td>
                    <td>{charge.reason || 'N/A'}</td>
                    <td>{formatDate(charge.addedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog
      header="Reservation Details"
      visible={visible}
      onHide={onHide}
      footer={renderFooter()}
      style={{ width: '90%', maxWidth: '900px' }}
      breakpoints={{ '768px': '95vw' }}
    >
      {renderContent()}
    </Dialog>
  );
};

export default ReservationDetailsModal;
